import { ApiModel, ApiResource } from 'lib/interfaces/api';

export interface InsightQueryAttributes {
  name: string;
  query: string;
  status: string;
  'created-at': string;
  'updated-at': string;
}

interface InsightQueryRelationships {}

export type InsightQuery = ApiResource<
  InsightQueryAttributes,
  InsightQueryRelationships,
  ApiModel.InsightQuery
>;

export type InsightQueriesPreviewResult = Record<string, string | null>;
export type InsightQueriesPreviewResults = InsightQueriesPreviewResult[];

export interface InsightQueriesPreviewResponse {
  meta: {
    results: InsightQueriesPreviewResults;
    error?: string;
  };
}

// Hardcoded schema definitions for various tables. These schemas will eventually be fetched directly from Snowflake.
export const InsightQueriesSchema = [
  {
    name: 'accounts',
    columns: [
      { name: 'archived_at', type: 'timestamp_ntz' },
      { name: 'created_at', type: 'timestamp_ntz' },
      { name: 'environment_id', type: 'varchar' },
      { name: 'fields_updated_at', type: 'timestamp_ntz' },
      { name: 'reference_id', type: 'varchar' },
      { name: 'id', type: 'varchar' },
      { name: 'updated_at', type: 'timestamp_ntz' },
    ],
  },
  {
    name: 'cases',
    columns: [
      { name: 'assigned_at', type: 'timestamp_ntz' },
      { name: 'assigner_type', type: 'varchar' },
      { name: 'case_number', type: 'float' },
      { name: 'case_template_id', type: 'varchar' },
      { name: 'created_at', type: 'timestamp_ntz' },
      { name: 'creator_type', type: 'varchar' },
      { name: 'enqueued_at', type: 'timestamp_ntz' },
      { name: 'environment_id', type: 'varchar' },
      { name: 'redacted_at', type: 'timestamp_ntz' },
      { name: 'resolution', type: 'varchar' },
      { name: 'resolved_at', type: 'timestamp_ntz' },
      { name: 'resolver_type', type: 'varchar' },
      { name: 'sla_expires_at', type: 'timestamp_ntz' },
      { name: 'status', type: 'varchar' },
      { name: 'id', type: 'varchar' },
      { name: 'triaged_at', type: 'timestamp_ntz' },
      { name: 'updated_at', type: 'timestamp_ntz' },
      { name: 'updater_type', type: 'varchar' },
    ],
  },
  {
    name: 'inquiries',
    columns: [
      { name: 'account_id', type: 'varchar' },
      { name: 'completed_at', type: 'timestamp_ntz' },
      { name: 'created_at', type: 'timestamp_ntz' },
      { name: 'decisioned_at', type: 'timestamp_ntz' },
      { name: 'environment_id', type: 'varchar' },
      { name: 'expired_at', type: 'timestamp_ntz' },
      { name: 'failed_at', type: 'timestamp_ntz' },
      { name: 'inquiry_template_id', type: 'varchar' },
      { name: 'marked_for_review_at', type: 'timestamp_ntz' },
      { name: 'redacted_at', type: 'timestamp_ntz' },
      { name: 'started_at', type: 'timestamp_ntz' },
      { name: 'status', type: 'varchar' },
      { name: 'id', type: 'varchar' },
      { name: 'updated_at', type: 'timestamp_ntz' },
    ],
  },
  {
    name: 'txns',
    columns: [
      { name: 'created_at', type: 'timestamp_ntz' },
      { name: 'environment_id', type: 'varchar' },
      { name: 'redacted_at', type: 'timestamp_ntz' },
      { name: 'reference_id', type: 'varchar' },
      { name: 'id', type: 'varchar' },
      { name: 'txn_status', type: 'varchar' },
      { name: 'txn_type_name', type: 'varchar' },
      { name: 'txn_type_id', type: 'varchar' },
      { name: 'updated_at', type: 'timestamp_ntz' },
    ],
  },
  {
    name: 'verifications',
    columns: [
      { name: 'account_id', type: 'varchar' },
      { name: 'completed_at', type: 'timestamp_ntz' },
      { name: 'country_code', type: 'varchar' },
      { name: 'created_at', type: 'timestamp_ntz' },
      { name: 'environment_id', type: 'varchar' },
      { name: 'inquiry_id', type: 'varchar' },
      { name: 'redacted_at', type: 'timestamp_ntz' },
      { name: 'status', type: 'varchar' },
      { name: 'submitted_at', type: 'timestamp_ntz' },
      { name: 'id', type: 'varchar' },
      { name: 'type', type: 'varchar' },
      { name: 'updated_at', type: 'timestamp_ntz' },
      { name: 'verification_template_id', type: 'varchar' },
      { name: 'verification_template_version_id', type: 'varchar' },
    ],
  },
];

export const SensitiveDataSchema = [
  {
    name: 'inquiries_fields_expanded',
    columns: [
      { name: 'id', type: 'varchar' },
      { name: 'field_name', type: 'varchar' },
      { name: 'field_value', type: 'variant' },
      { name: 'field_created_at', type: 'timestamp_ntz' },
      { name: 'field_updated_at', type: 'timestamp_ntz' },
    ],
  },
  {
    name: 'txns_fields_expanded',
    columns: [
      { name: 'id', type: 'varchar' },
      { name: 'field_name', type: 'varchar' },
      { name: 'field_value', type: 'variant' },
      { name: 'field_created_at', type: 'timestamp_ntz' },
      { name: 'field_updated_at', type: 'timestamp_ntz' },
    ],
  },
];

// Hardcoded sample queries for demo purposes.
// These should eventually be dynamic and tailored to the customer's integration/use case.
// Consider moving these to a Persona-defined "Saved Queries" feature once implemented.
export const RecommendedQueries = [
  {
    product: 'Inquiry',
    queries: [
      {
        title: 'How many inquiries are currently approved?',
        query: `SELECT
  COUNT(*) as approved_inquiries
FROM
  INQUIRIES
WHERE
  status = 'approved'`,
      },
      {
        title: 'How many inquiries have failed verifications?',
        query: `SELECT
  COUNT(*) as count
FROM
  INQUIRIES
LEFT JOIN
    VERIFICATIONS ON VERIFICATIONS.INQUIRY_ID = INQUIRIES.ID
WHERE
  VERIFICATIONS.status = 'failed'`,
      },
    ],
  },
  {
    product: 'Cases',
    queries: [
      {
        title: 'What is the average time it takes to resolve a Case from creation?',
        query: `SELECT
    ROUND(AVG(DATEDIFF('hour', CREATED_AT, RESOLVED_AT)), 1) AS avg_hours_created_to_resolved
FROM
    CASES
WHERE
    RESOLVED_AT IS NOT NULL
      AND CREATED_AT IS NOT NULL`,
      },
      {
        title: 'What is the SLA compliance rate for Cases?',
        query: `SELECT
    COUNT(*) AS total_cases,
    COUNT(CASE WHEN RESOLVED_AT <= SLA_EXPIRES_AT THEN 1 END) AS sla_compliant_cases,
    ROUND((COUNT(CASE WHEN RESOLVED_AT <= SLA_EXPIRES_AT THEN 1 END) * 100.0) / COUNT(*), 2) AS sla_compliance_rate
FROM
    CASES
WHERE
    SLA_EXPIRES_AT IS NOT NULL`,
      },
    ],
  },
  {
    product: 'Verifications',
    queries: [
      {
        title: 'How many Government ID NFC verifications passed?',
        query: `SELECT COUNT(*) as count FROM VERIFICATIONS WHERE type = 'Verification::GovernmentIdNfc' and status = 'passed'`,
      },
    ],
  },
];
